/**
 * Shows a document inside the page: PDFs page by page, photos as images, and spreadsheets,
 * Word, CSV and text files from their preview data. Anything else offers a download.
 */
import { useQuery } from "@tanstack/react-query";
import { Download } from "lucide-react";
import { lazy, Suspense, useEffect, useState } from "react";
import { Loading } from "@/components/page";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { errorMessage } from "@/lib/api";
import { TableScroll } from "@/components/table-scroll";

const PdfPages = lazy(() => import("./pdf-view").then((m) => ({ default: m.PdfPages })));

type ViewedDoc = { id: string; filename: string; contentType: string };

type Preview =
  | { kind: "table"; sheets: Array<{ name: string; rows: string[][] }>; truncated: boolean }
  | { kind: "text"; paragraphs: string[]; truncated: boolean };

const PREVIEWABLE = new Set([
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "text/csv",
  "text/plain",
]);

function viewKind(d: ViewedDoc): "pdf" | "image" | "preview" | "none" {
  if (d.contentType === "application/pdf") return "pdf";
  // HEIC can't be drawn by most browsers.
  if (d.contentType === "image/jpeg" || d.contentType === "image/png") return "image";
  return PREVIEWABLE.has(d.contentType) ? "preview" : "none";
}

function DownloadLink({ href, filename }: { href: string; filename: string }) {
  return (
    <a href={href} className="inline-flex items-center gap-1.5 rounded-md border border-input bg-background px-3 py-1.5 text-sm hover:bg-muted" aria-label={`Download ${filename}`}>
      <Download className="h-4 w-4" /> Download
    </a>
  );
}

export function DocumentViewer({ doc, portal }: { doc: ViewedDoc; portal?: boolean }) {
  const base = `${portal ? "/api/portal/documents" : "/api/documents"}/${doc.id}`;
  const kind = viewKind(doc);
  const [pages, setPages] = useState<number | null>(null);
  const [failed, setFailed] = useState(false);
  return (
    <section aria-label={`Viewing ${doc.filename}`} className="min-w-0 space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <h3 className="truncate text-sm font-semibold">{doc.filename}</h3>
          {kind === "pdf" && pages !== null && (
            <p className="text-xs text-muted-foreground" aria-live="polite">
              {pages} page{pages === 1 ? "" : "s"}
            </p>
          )}
        </div>
        <DownloadLink href={`${base}/download`} filename={doc.filename} />
      </div>
      {kind === "pdf" ? (
        <Suspense fallback={<Loading />}>
          <PdfPages url={`${base}/content`} onLoaded={setPages} onError={() => setFailed(true)} />
          {failed && <p className="text-sm text-muted-foreground">Use Download to open it on your device.</p>}
        </Suspense>
      ) : kind === "image" ? (
        <ImageView url={`${base}/content`} doc={doc} />
      ) : kind === "preview" ? (
        <PreviewView url={`${base}/preview`} />
      ) : (
        <p className="text-sm text-muted-foreground">This file can't be shown here. Download it to open it on your device.</p>
      )}
    </section>
  );
}

/** Fetches the bytes and shows them from a blob URL typed from the document's own content type. */
function ImageView({ url, doc }: { url: string; doc: ViewedDoc }) {
  const [src, setSrc] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    let made: string | null = null;
    setSrc(null);
    setError(null);
    fetch(url, { credentials: "same-origin" })
      .then(async (res) => {
        if (!res.ok) {
          const body = (await res.json().catch(() => ({}))) as { error?: { message?: string } };
          throw new Error(body.error?.message ?? "We couldn't open this file.");
        }
        const blob = new Blob([await res.arrayBuffer()], { type: doc.contentType });
        if (cancelled) return;
        made = URL.createObjectURL(blob);
        setSrc(made);
      })
      .catch((e: Error) => !cancelled && setError(e.message));
    return () => {
      cancelled = true;
      if (made) URL.revokeObjectURL(made);
    };
  }, [url, doc.contentType]);
  if (error) return <p className="text-sm text-destructive">{error}</p>;
  if (!src) return <Loading />;
  return <img src={src} alt={doc.filename} className="mx-auto h-auto max-w-full rounded ring-1 ring-border" />;
}

function PreviewView({ url }: { url: string }) {
  const { data, error, isLoading } = useQuery<Preview>({ queryKey: [url], staleTime: 60_000 });
  const [sheet, setSheet] = useState(0);
  if (isLoading) return <Loading />;
  if (error || !data) return <p className="text-sm text-muted-foreground">{error ? errorMessage(error) : "This file can't be shown here."}</p>;
  const note = data.truncated && <p className="text-xs text-muted-foreground">Only the start of this file is shown. Download it to see all of it.</p>;
  if (data.kind === "text") {
    return (
      <div className="space-y-2">
        <div className="max-h-[60vh] overflow-y-auto rounded-md border border-border p-3 text-sm">
          {data.paragraphs.map((p, i) => (p === "" ? <div key={i} className="h-3" aria-hidden /> : <p key={i} className="whitespace-pre-wrap break-words">{p}</p>))}
          {data.paragraphs.length === 0 && <p className="text-muted-foreground">This file has no text.</p>}
        </div>
        {note}
      </div>
    );
  }
  const current = data.sheets[Math.min(sheet, data.sheets.length - 1)]!;
  return (
    <div className="min-w-0 space-y-2">
      {data.sheets.length > 1 && (
        <div className="-mx-1 flex gap-1 overflow-x-auto px-1 pb-1" role="tablist" aria-label="Sheets">
          {data.sheets.map((s, i) => (
            <button
              key={i}
              type="button"
              role="tab"
              aria-selected={s === current}
              onClick={() => setSheet(i)}
              className={`shrink-0 rounded-full border px-3 py-1 text-xs ${s === current ? "border-primary bg-primary text-primary-foreground" : "border-input hover:bg-muted"}`}
            >
              {s.name}
            </button>
          ))}
        </div>
      )}
      {/* The table scrolls inside its box, so a wide sheet never widens the page. */}
      <TableScroll label={`${current.name} sheet`} both className="max-h-[60vh] max-w-full rounded-md border border-border">
        {current.rows.length === 0 ? (
          <p className="p-3 text-sm text-muted-foreground">This sheet is empty.</p>
        ) : (
          <table className="border-collapse text-xs" aria-label={current.name}>
            <tbody>
              {current.rows.map((r, i) => (
                <tr key={i} className="even:bg-muted/40">
                  <th scope="row" className="sticky left-0 border-r border-border bg-secondary px-2 py-1 text-right font-normal text-muted-foreground">
                    {i + 1}
                  </th>
                  {r.map((c, j) => (
                    <td key={j} className="max-w-[16rem] truncate whitespace-nowrap border-b border-border px-2 py-1" title={c.length > 40 ? c : undefined}>
                      {c}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </TableScroll>
      {note}
    </div>
  );
}

/** The Preview dialog used by the document lists. */
export function DocumentPreviewDialog({ doc, portal, onClose }: { doc: ViewedDoc; portal?: boolean; onClose: () => void }) {
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] w-[calc(100vw-2rem)] max-w-4xl grid-cols-[minmax(0,1fr)] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Preview</DialogTitle>
          <DialogDescription className="sr-only">{doc.filename}</DialogDescription>
        </DialogHeader>
        <DocumentViewer doc={doc} portal={portal} />
      </DialogContent>
    </Dialog>
  );
}
